import React, { useMemo, useState } from "react";
import { ChevronDown, Inbox, Paperclip, Plus } from "lucide-react";
import type { LeaveRequest } from "../../controller/mobile/Izintypes";
import { LEAVE_STATUS_META, LEAVE_TYPE_LABEL } from "../../controller/mobile/Izintypes";
import { monthKey, monthLabel } from "../../controller/mobile/Dateutils";

interface IzinListPageProps {
  requests: LeaveRequest[];
  onAddNew: () => void;
}
 
interface MonthGroup {
  key: string;
  label: string;
  requests: LeaveRequest[];
}
 
const formatDMY = (value: string | Date): string => {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};
 
export default function IzinListPage({
  requests,
  onAddNew,
}: IzinListPageProps): React.ReactElement {
  const groups = useMemo<MonthGroup[]>(() => {
    const sorted = [...requests].sort((a, b) =>
      a.startDate < b.startDate ? 1 : -1
    );
 
    const map = new Map<string, MonthGroup>();
    for (const req of sorted) {
      const key = monthKey(req.startDate);
      const existing = map.get(key);
      if (existing) {
        existing.requests.push(req);
      } else {
        map.set(key, {
          key,
          label: monthLabel(req.startDate),
          requests: [req],
        });
      }
    }
    return Array.from(map.values());
  }, [requests]);
 
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    groups.length > 0 ? { [groups[0].key]: true } : {}
  );
 
  const toggleMonth = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };
 
  return (
    <div className="flex h-full flex-col">
      {/* Header — flat style, konsisten dengan tab Home/History lain */}
      <header className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
        <h1 className="text-lg font-semibold text-slate-900">
          Berhalangan Hadir
        </h1>
        <button
          type="button"
          onClick={onAddNew}
          className="flex items-center gap-1 rounded-full bg-blue-950 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-900"
        >
          <Plus className="h-3.5 w-3.5" />
          Ajukan
        </button>
      </header>
 
      {/* Content */}
      <main className="flex-1 overflow-y-auto px-5 py-5">
        {groups.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-slate-400">
            <Inbox className="h-8 w-8" />
            <p className="text-sm">Belum ada pengajuan.</p>
            <button
              type="button"
              onClick={onAddNew}
              className="mt-2 text-sm font-medium text-blue-950 underline underline-offset-4"
            >
              Ajukan sekarang
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((group) => {
              const isOpen = !!expanded[group.key];
 
              return (
                <section
                  key={group.key}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                >
                  <button
                    type="button"
                    onClick={() => toggleMonth(group.key)}
                    className="flex w-full items-center justify-between px-4 py-3.5"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">
                        {group.label}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                        {group.requests.length} pengajuan
                      </span>
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
 
                  {isOpen && (
                    <div className="space-y-2 border-t border-slate-100 px-4 pb-4 pt-3">
                      {group.requests.map((req) => {
                        // some responses use a different field name or are untyped; coerce safely
                        const statusKey = (req as any).penyetujuan_aktivitas ?? (req as any).status;
                        const statusMeta = LEAVE_STATUS_META[
                          (statusKey as unknown) as keyof typeof LEAVE_STATUS_META
                        ] || { label: String(statusKey ?? "Unknown"), className: "" };
                        const dateRangeLabel =
                          req.startDate === req.endDate
                            ? formatDMY(req.startDate)
                            : `${formatDMY(req.startDate)} s/d ${formatDMY(
                                req.endDate
                              )}`;
 
                        return (
                          <div
                            key={req.id}
                            className="rounded-xl border border-slate-100 bg-slate-50 p-3.5"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="font-mono text-xs text-slate-500">
                                {dateRangeLabel}
                              </p>
                              <span
                                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide ${statusMeta.className}`}
                              >
                                {statusMeta.label}
                              </span>
                            </div>
 
                            <p className="mt-1.5 text-sm font-semibold text-slate-800">
                              {LEAVE_TYPE_LABEL[req.type]}
                            </p>
 
                            {req.keterangan && (
                              <p className="mt-0.5 text-xs text-slate-500">
                                {req.keterangan}
                              </p>
                            )}
 
                            {req.attachmentName && (
                              <div className="mt-1.5 flex items-center gap-1 text-xs font-medium text-blue-700">
                                <Paperclip className="h-3 w-3" />
                                <span className="truncate">
                                  {req.attachmentName}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}